/**
 * Select Component
 * Dropdown selection component using Radix UI
 */

import * as React from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { createPortal } from 'react-dom';

export interface SelectProps {
  value?: string;
  onValueChange?: (value: string) => void;
  children: React.ReactNode;
  disabled?: boolean;
}

export interface SelectTriggerProps {
  children: React.ReactNode;
  className?: string;
}

export interface SelectContentProps {
  children: React.ReactNode;
  className?: string;
}

export interface SelectItemProps {
  value: string;
  children: React.ReactNode;
  className?: string;
}

export interface SelectValueProps {
  placeholder?: string;
}

function getSelectItemLabel(children: React.ReactNode, selectedValue?: string): React.ReactNode | undefined {
  if (!selectedValue) return undefined;

  let resolvedLabel: React.ReactNode | undefined;

  const visit = (nodes: React.ReactNode): void => {
    React.Children.forEach(nodes, (child) => {
      if (resolvedLabel !== undefined || !React.isValidElement(child)) {
        return;
      }

      const element = child as React.ReactElement<{
        children?: React.ReactNode;
        value?: string;
      }>;

      if (element.type === SelectItem && element.props.value === selectedValue) {
        resolvedLabel = element.props.children;
        return;
      }

      if (element.props.children) {
        visit(element.props.children);
      }
    });
  };

  visit(children);
  return resolvedLabel;
}

const SelectContext = React.createContext<{
  value?: string;
  selectedLabel?: React.ReactNode;
  onValueChange?: (value: string) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
}>({
  open: false,
  setOpen: () => {},
  triggerRef: { current: null },
});

export function Select({ value, onValueChange, children, disabled }: SelectProps) {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const selectedLabel = React.useMemo(() => getSelectItemLabel(children, value), [children, value]);

  return (
    <SelectContext.Provider value={{ value, selectedLabel, onValueChange, open, setOpen, triggerRef }}>
      <div className="relative inline-block w-full">{children}</div>
    </SelectContext.Provider>
  );
}

export function SelectTrigger({ children, className = '' }: SelectTriggerProps) {
  const { open, setOpen, triggerRef } = React.useContext(SelectContext);

  return (
    <button
      ref={triggerRef}
      type="button"
      onClick={() => setOpen(!open)}
      className={`flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    >
      {children}
      <ChevronDown className="h-4 w-4 opacity-50" />
    </button>
  );
}

export function SelectValue({ placeholder }: SelectValueProps) {
  const { value, selectedLabel } = React.useContext(SelectContext);

  if (!value && placeholder) {
    return <span className="text-muted-foreground">{placeholder}</span>;
  }

  if (selectedLabel !== undefined) {
    return <span className="truncate">{selectedLabel}</span>;
  }

  return <span>{value}</span>;
}

export function SelectContent({ children, className = '' }: SelectContentProps) {
  const { open, setOpen, triggerRef } = React.useContext(SelectContext);
  const contentRef = React.useRef<HTMLDivElement>(null);

  const updatePosition = React.useCallback(() => {
    const trigger = triggerRef.current;
    const content = contentRef.current;
    if (!trigger || !content) return;

    const rect = trigger.getBoundingClientRect();
    content.style.top = `${rect.bottom + 4}px`;
    content.style.left = `${rect.left}px`;
    content.style.width = `${rect.width}px`;
  }, [triggerRef]);

  React.useEffect(() => {
    if (!open) return;

    const frame = requestAnimationFrame(updatePosition);

    const handleReposition = () => updatePosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [open, updatePosition]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        contentRef.current &&
        !contentRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, setOpen, triggerRef]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={contentRef}
      className={`fixed z-[90] max-h-60 overflow-auto rounded-md border border-border bg-popover text-popover-foreground shadow-lg ${className}`}
    >
      {children}
    </div>,
    document.body
  );
}

export function SelectItem({ value, children, className = '' }: SelectItemProps) {
  const { value: selectedValue, onValueChange, setOpen } = React.useContext(SelectContext);
  const isSelected = selectedValue === value;

  const handleClick = () => {
    onValueChange?.(value);
    setOpen(false);
  };

  return (
    <div
      onClick={handleClick}
      className={`relative flex w-full cursor-pointer select-none items-center rounded-sm py-2 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground ${
        isSelected ? 'bg-accent/70 text-accent-foreground' : ''
      } ${className}`}
    >
      {isSelected && (
        <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
          <Check className="h-4 w-4 text-primary" />
        </span>
      )}
      {children}
    </div>
  );
}
